import { useEffect, useState, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { billingApi } from '@/api/endpoints'
import { formatDate } from '@/lib/utils'

import { forgetCoupon } from './coupon'
import { formatCount, PERIOD_NOUN } from './planUi'
import { SUBSCRIPTION_KEY } from './useLicense'

/** How long to wait for Stripe's webhook to switch the plan on. */
const WAIT_MS = 30_000
const POLL_MS = 2_000

/**
 * Stripe Checkout returns here (`?session_id=`). The subscription is switched
 * on by Stripe's webhook, a moment later: poll until it is active.
 */
export function BillingSuccessPage() {
  const [params] = useSearchParams()
  const sessionId = params.get('session_id')
  const [startedAt] = useState(() => Date.now())
  const [timedOut, setTimedOut] = useState(false)

  useEffect(() => {
    const timer = window.setTimeout(() => setTimedOut(true), WAIT_MS)
    return () => window.clearTimeout(timer)
  }, [startedAt])

  const query = useQuery({
    queryKey: SUBSCRIPTION_KEY,
    queryFn: billingApi.subscription,
    retry: false,
    refetchInterval: (q) => (q.state.data?.active || timedOut ? false : POLL_MS),
    refetchOnMount: 'always',
  })
  const data = query.data
  const sub = data?.subscription
  const paid = Boolean(data?.active && sub)

  // The subscription the code was for has started: do not offer the code again.
  useEffect(() => {
    if (paid) forgetCoupon()
  }, [paid])

  if (data?.active && sub) {
    return (
      <ReturnShell
        tone="success"
        eyebrow="Payment received"
        title={`You're on ${sub.planName}`}
        body={
          <>
            {PERIOD_NOUN[sub.period]} billing.{' '}
            {data.usage ? (
              <>
                <span className="num">{formatCount(data.usage.limit)}</span> applications this month,{' '}
              </>
            ) : null}
            {sub.renewsAt ? `renews ${formatDate(sub.renewsAt)}.` : `until ${formatDate(sub.currentPeriodEnd)}.`} A receipt is on its
            way to your inbox.
          </>
        }
        actions={
          <>
            <Link to="/dashboard/jobs" className={PRIMARY}>
              Start applying
            </Link>
            {data.features.autoApply && (
              <Link to="/dashboard/auto-apply" className={SECONDARY}>
                Set up Auto Apply
              </Link>
            )}
            <Link to="/dashboard/profile/subscription" className={SECONDARY}>
              View subscription
            </Link>
          </>
        }
      />
    )
  }

  if (timedOut || query.isError) {
    return (
      <ReturnShell
        tone="warning"
        eyebrow="Almost there"
        title="Your payment is still being confirmed"
        body="Stripe can take a minute to tell us. Your plan switches on by itself as soon as it does; there is no need to pay again."
        actions={
          <>
            <button
              type="button"
              className={PRIMARY}
              onClick={() => {
                setTimedOut(false)
                void query.refetch()
              }}
            >
              Check again
            </button>
            <Link to="/dashboard/profile/subscription" className={SECONDARY}>
              View subscription
            </Link>
          </>
        }
        footnote={sessionId ? `Reference: ${sessionId.slice(-12)}` : undefined}
      />
    )
  }

  return (
    <ReturnShell
      tone="brand"
      eyebrow="Payment received"
      title="Switching your plan on"
      body="This takes a few seconds. Keep this page open."
      spinner
    />
  )
}

/** Stripe Checkout's "back" link: nothing was bought. */
export function BillingCancelPage() {
  return (
    <ReturnShell
      tone="neutral"
      eyebrow="Checkout cancelled"
      title="No charge was made"
      body="You left checkout before paying. Pick a plan whenever you are ready."
      actions={
        <>
          <Link to="/dashboard/plans" className={PRIMARY}>
            Back to plans
          </Link>
          <Link to="/dashboard" className={SECONDARY}>
            Go to Home
          </Link>
        </>
      }
    />
  )
}

const PRIMARY =
  'inline-flex h-11 items-center justify-center rounded-field bg-ink px-5 text-[15px] font-medium text-oncolor transition-colors hover:bg-ink/90'
const SECONDARY =
  'inline-flex h-11 items-center justify-center rounded-field border border-line bg-surface px-5 text-[15px] font-medium text-ink transition-colors hover:border-line-strong hover:bg-surface-muted'

const TONE_RING = {
  success: 'bg-success/12 text-success',
  warning: 'bg-warning/14 text-warning',
  brand: 'bg-brand-soft text-brand',
  neutral: 'bg-ink/[0.06] text-ink-soft',
} as const

function ReturnShell({
  tone,
  eyebrow,
  title,
  body,
  actions,
  spinner,
  footnote,
}: {
  tone: keyof typeof TONE_RING
  eyebrow: string
  title: string
  body: ReactNode
  actions?: ReactNode
  spinner?: boolean
  footnote?: string
}) {
  return (
    <div className="flex min-h-[60vh] items-center justify-center py-6">
      <section className="card w-full max-w-[480px] p-6 text-center sm:p-8" aria-live="polite">
        <span className={`mx-auto grid h-12 w-12 place-items-center rounded-full ${TONE_RING[tone]}`}>
          {spinner ? (
            <svg className="h-6 w-6 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" opacity="0.25" />
              <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          ) : tone === 'success' ? (
            <svg className="h-6 w-6" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="m3.5 8.5 3 3 6-7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg className="h-6 w-6" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.5" />
              <path d="M10 6.5v4.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              <circle cx="10" cy="13.75" r="0.9" fill="currentColor" />
            </svg>
          )}
        </span>
        <p className="label-eyebrow mt-5">{eyebrow}</p>
        <h1 className="mt-2 text-[22px] font-semibold leading-tight tracking-[-0.02em] text-ink sm:text-[24px]">{title}</h1>
        <p className="mt-2 text-[14px] leading-relaxed text-ink-muted">{body}</p>
        {actions && <div className="mt-6 flex flex-col gap-2 xs:flex-row xs:flex-wrap xs:justify-center">{actions}</div>}
        {footnote && <p className="num mt-4 text-[11.5px] text-ink-faint">{footnote}</p>}
      </section>
    </div>
  )
}
